import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Form, PageHeader, Popconfirm, Spin } from "antd";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import copy from 'copy-to-clipboard';
import _ from "lodash";
import moment from 'moment';

import { cancelInvoice, fetchInvoice, fetchPublicInvoice, forwardInvoice, generatePublicIdInvoice, sendNotification } from "../../../redux/actions";
import { companyAddressLines, formatAddress, primaryAddress } from "../../../helpers/address";
import Modal from "antd/lib/modal/Modal";

export default ({ data = null, publicInvoice = false }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [forwardModalVisible, setForwardModalVisible] = useState(false);
  const [jobs, setJobs] = useState([]);

  const [forwardForm] = Form.useForm();

  const config = useSelector((state) => state.config, shallowEqual);
  const [invoice, setInvoice] = useState(data);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const renderDate = (date) => moment(date).format('DD/MM/YYYY');
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    };
    (publicInvoice ? dispatch(fetchPublicInvoice(id)) : dispatch(fetchInvoice(id)))
      .then(({ payload }) => {
        setInvoice(payload);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!invoice) return;
    const invoiceJobs = _.get(invoice, "jobs", []).flatMap((x) => {
      x.items.forEach((i) => i.startDate = x.startDate);
      return x.items;
    });
    if (invoice.subscription) {
      const newJobs = invoiceJobs.map((j) => {
        j.unitCost = 0;
        return j;
      });
      newJobs.push({
        item: {
          title: intl.formatMessage({ id: 'subscription' })
        },
        qty: 1,
        unitCost: invoice.breakdown.netTotal,
        _id: 1,
      });
      setJobs(newJobs);
    } else {
      setJobs(invoiceJobs);
    }
  }, [invoice]);

  if (loading) return <Spin style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} size="large" />;

  const showDates = !!invoice?.showDates;

  const render2Decimal = (number) => {
    if (!number) return 0;
    return _.round(number, 2).toFixed(2);
  }

  const onCancel = () => {
    setCancelling(true);
    dispatch(cancelInvoice(id))
      .then(() => history.push("/invoices"))
      .catch(() => setCancelling(false));
  };

  const onForward = async () => {
    await forwardForm.validateFields();
    const { emails } = forwardForm.getFieldsValue();
    setForwarding(true);
    dispatch(forwardInvoice(id, emails))
      .finally(() => {
        setForwarding(false);
        onCancelModal();
      });
  };

  const onCancelModal = () => {
    setForwardModalVisible(false);
    forwardForm.resetFields();
  };

  const generatePublicId = () => {
    dispatch(generatePublicIdInvoice(id)).then((res) => setInvoice({ ...invoice, publicId: res.payload }));
  }

  return (
    <div className="page-wrapper">
      <Modal
        title={intl.formatMessage({ id: 'forward' })}
        visible={forwardModalVisible}
        onOk={onForward}
        onCancel={onCancelModal}
        okText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
      >
        <Form form={forwardForm}>
          <Form.Item name="emails" rules={[{ required: true, message: intl.formatMessage({ id: "fieldRequired" }) }]}>
            <Checkbox.Group>
              {_.get(invoice, 'client.emails', []).map(e => (
                <div key={e._id}>
                  <Checkbox value={e.emailAddress}>
                    {e.emailAddress} ({e.emailType})
                  </Checkbox>
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
      <PageHeader
        className="site-page-header-ghost-wrapper"
        ghost={false}
        title={intl.formatMessage({ id: "viewInvoice" })}
        extra={[
          !publicInvoice && !invoice.publicId && <Button
            key="generatePublicId"
            type="primary"
            onClick={() => generatePublicId()}
          >
            {intl.formatMessage({ id: "generatePublicId" })}
          </Button>,
          !publicInvoice && invoice.publicId && <Button
            key="copyLink"
            type="primary"
            onClick={() => {
              copy(`https://dnacleaning.com.cy/invoices/public/${invoice.publicId}`);
              dispatch(sendNotification({
                type: 'success',
                message: "copied",
                description: "linkCopiedToClipboard"
              }))
            }}
          >
            {intl.formatMessage({ id: "copyLink" })}
          </Button>,
          !publicInvoice && <Button
            key="forward"
            type="primary"
            disabled={forwarding || invoice.status === 'cancelled'}
            onClick={() => setForwardModalVisible(true)}
          >
            {intl.formatMessage({ id: "forward" })}
          </Button>,
           <Button
            key="edit"
            type="primary"
            onClick={() => history.push(`/invoices/edit/${id}`)}
          >
            {intl.formatMessage({ id: "edit" })}
          </Button>,
          <Button
            key="print"
            type="primary"
            onClick={handlePrint}
          >
            {intl.formatMessage({ id: "print" })}
          </Button>,
          !publicInvoice && (<Popconfirm
            key="delete"
            title={intl.formatMessage({ id: "cancelConfirmation" })}
            onConfirm={onCancel}
            onCancel={() => null}
            okText={intl.formatMessage({ id: "yes" })}
            cancelText={intl.formatMessage({ id: "no" })}
            disabled={cancelling || (invoice.status === 'cancelled')}
          >
            <Button
              key="cancel"
              type="danger"
              disabled={cancelling || (invoice.status === 'cancelled')}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
          </Popconfirm>)
        ]}
      />
      <div className="invoice invoice-sheet" style={{ border: "1px solid #f5f5f5" }} ref={componentRef}>
        <div className="invoice-main">
        <div className="invoice-header">
        <div className="invoice-top">
          <img src="/images/dna.png" alt="logo" />
          <div className="invoice-title">
            <strong>{intl.formatMessage({ id: "invoice" })}</strong>
            <div>{intl.formatMessage({ id: "invoiceNum" })}: {_.get(invoice, "_ref")}</div>
            <div>{intl.formatMessage({ id: "date" })}: {renderDate(_.get(invoice, "date"))}</div>
          </div>
        </div>
        <div className="invoice-parties">
          <div>
            <div className="invoice-label">{intl.formatMessage({ id: "from" })}</div>
            <strong>{config.companyName}</strong>
            {companyAddressLines(config.address).map((line) => (
              <div key={line}>{line}</div>
            ))}
            <div>{intl.formatMessage({ id: "phoneSmall" })}: {config.phone}</div>
            <div>{intl.formatMessage({ id: "vat" })}: {config.taxID}</div>
            <div>{config.emailAddress}</div>
          </div>
          <div>
            <div className="invoice-label">{intl.formatMessage({ id: "client" })}</div>
            <strong>
              {_.get(invoice, "client.companyName")
                || `${_.get(invoice, "client.firstName", "")} ${_.get(invoice, "client.lastName", "")}`.trim()}
            </strong>
            <div>{formatAddress(primaryAddress(invoice.client))}</div>
            <div>
              {intl.formatMessage({ id: "name" })}:{" "}
              {_.get(invoice, "client.firstName", "")} {_.get(invoice, "client.lastName", "")}
            </div>
            <div>
              {intl.formatMessage({ id: "phoneSmall" })}:{" "}
              {_.get(invoice, "client.phones", [])
                .filter((p) => p.phoneType !== "Fax")
                .map((p) => p.phoneNumber)
                .join(", ")}
            </div>
            <div>
              {_.get(invoice, "client.emails", []).map((e) => e.emailAddress).join(", ")}
            </div>
          </div>
        </div>
        </div>
        <table className="invoice-lines">
          <thead>
            <tr>
              <th>{intl.formatMessage({ id: "productService" })}</th>
              {showDates && <th>{intl.formatMessage({ id: "date" })}</th>}
              <th className="num">{intl.formatMessage({ id: "qty" })}</th>
              <th className="num">{intl.formatMessage({ id: "unitCost" })}</th>
              <th className="num">{intl.formatMessage({ id: "total" })}</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={_.get(job, "_id")}>
                <td>
                  {_.get(job, "item.title")}
                  {_.get(job, "notes") ? <span style={{ fontStyle: "italic" }}> — {job.notes}</span> : ""}
                </td>
                {showDates && <td>{_.get(job, "startDate") ? renderDate(job.startDate) : ""}</td>}
                <td className="num">{job.qty}</td>
                <td className="num">€{render2Decimal(job.unitCost)}</td>
                <td className="num">€{render2Decimal((job.qty || 0) * (job.unitCost || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="invoice-footer">
        <div className="invoice-bottom">
          <div>
            <div className="invoice-label">{intl.formatMessage({ id: "notes" })}</div>
            <div><strong>{intl.formatMessage({ id: "bankAccountNumber" })}:</strong> {String(_.get(config, "bankAccountNumber", "")).split("SWIFT/BIC")[0].replace(/[\s-]+$/, "")}</div>
            <div><strong>{intl.formatMessage({ id: "bankDetails" })}:</strong> {_.get(config, "bankDetails", "")}</div>
            <div><strong>IBAN:</strong> {_.get(config, "iban", "")}</div>
            <div><strong>SWIFT/BIC:</strong> BCYPCY2N</div>
            {_.get(invoice, "notes") && <div style={{ marginTop: 4 }}>{invoice.notes}</div>}
          </div>
          <table className="invoice-totals">
            <tbody>
              <tr>
                <td>{intl.formatMessage({ id: "subTotal" })}</td>
                <td>€{render2Decimal(_.get(invoice, "breakdown.subTotal"))}</td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: "discount" })}</td>
                <td>€{render2Decimal(_.get(invoice, "breakdown.discount"))}</td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: "totalNetWorth" })}</td>
                <td>€{render2Decimal(_.get(invoice, "breakdown.netTotal"))}</td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: "vat" })} ({config.taxValue}%)</td>
                <td>€{render2Decimal(_.get(invoice, "breakdown.taxTotal"))}</td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: "total" })}</td>
                <td>€{render2Decimal(_.get(invoice, "breakdown.total"))}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 20 }}>{intl.formatMessage({ id: "receivedBy" })}</div>
        <div className="invoice-sign">
          <div>
            <div className="line" />
            <div>{intl.formatMessage({ id: "name" })}</div>
          </div>
          <div>
            <div className="line" />
            <div>{intl.formatMessage({ id: "date" })}</div>
          </div>
          <div>
            <div className="line" />
            <div>{intl.formatMessage({ id: "signature" })}</div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
